import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Sidebar } from '@/components/shell/Sidebar';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

import { patientSidebarGroups } from './patientSidebar';

interface PatientRecordPendingStateProps {
  /**
   * The patient guid from the URL. Lets us render the actual sidebar
   * (its links are derived from the section registry + guid, not from
   * patient data) so the pending → loaded transition only swaps the
   * band, summary strip, and section bodies — not the chrome around
   * them.
   */
  readonly guid: string;
}

/**
 * First-paint placeholder while the patient detail query is in flight.
 * Mirrors `PatientRecordView`'s layout exactly — sidebar, two-row
 * identity band, summary strip, and section column — so the page
 * doesn't reflow when data arrives. Renders the real Sidebar (which
 * doesn't depend on patient data) and skeleton content for everything
 * the patient record drives.
 */
export function PatientRecordPendingState({
  guid,
}: PatientRecordPendingStateProps): ReactElement {
  const { t } = useTranslation();

  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={t('routes.patient.loading')}
      className="flex h-full"
    >
      <Sidebar groups={patientSidebarGroups(guid, t)} />
      <main className="flex-1 flex flex-col min-w-0 min-h-0 overflow-clip">
        <IdentityBandSkeleton />
        <SummaryStripSkeleton />
        <SubTabsSkeleton />
        <div className="flex-1 min-h-0 overflow-y-auto bg-background">
          <div className="px-8 py-4 grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] gap-6 max-w-[1400px] mx-auto">
            <div className="space-y-3 min-w-0">
              <SectionCardSkeleton headerLines={2} bodyLines={4} />
              <SectionCardSkeleton headerLines={1} bodyLines={3} />
              <SectionCardSkeleton headerLines={1} bodyLines={3} />
            </div>
            <SidePanelsSkeleton />
          </div>
        </div>
      </main>
    </div>
  );
}

function IdentityBandSkeleton(): ReactElement {
  return (
    <header aria-hidden="true" className="bg-card border-b border-border">
      <div className="px-8 pt-2.5 pb-4 flex items-start gap-5">
        <Skeleton className="h-12 w-12 rounded-lg" />
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex items-baseline flex-wrap gap-x-3 gap-y-1">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-32" />
          </div>
          <Skeleton className="h-4 w-2/3 max-w-md" />
        </div>
        <div className="flex items-center gap-3 shrink-0 self-center">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-9 w-24 rounded-md" />
          <Skeleton className="h-9 w-24 rounded-md" />
        </div>
      </div>
    </header>
  );
}

function SummaryStripSkeleton(): ReactElement {
  return (
    <div
      aria-hidden="true"
      className="border-b border-border bg-muted/30 px-6 py-1.5"
    >
      <Skeleton className="h-3.5 w-72" />
    </div>
  );
}

function SubTabsSkeleton(): ReactElement {
  return (
    <div
      aria-hidden="true"
      className="bg-card border-b border-border px-8 flex items-center gap-6 h-11"
    >
      {Array.from({ length: 6 }, (_, idx) => (
        <Skeleton key={idx} className="h-3.5 w-20" />
      ))}
    </div>
  );
}

function SidePanelsSkeleton(): ReactElement {
  return (
    <aside aria-hidden="true" className="hidden xl:flex flex-col gap-3">
      {[2, 3, 2].map((rows, idx) => (
        <Card key={idx} className="overflow-hidden">
          <div className="px-4 py-2.5 border-b border-border flex items-center gap-2">
            <Skeleton className="h-3.5 w-3.5 rounded" />
            <Skeleton className="h-3 w-24" />
          </div>
          {Array.from({ length: rows }, (_, rowIdx) => (
            <div
              key={rowIdx}
              className="px-4 py-2.5 border-b border-border last:border-b-0 flex items-center gap-2.5"
            >
              <Skeleton className="h-7 w-7 rounded-full" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-3 w-3/4" />
                <Skeleton className="h-2.5 w-1/2" />
              </div>
            </div>
          ))}
        </Card>
      ))}
    </aside>
  );
}

interface SectionCardSkeletonProps {
  readonly headerLines?: number;
  readonly bodyLines?: number;
}

function SectionCardSkeleton({
  headerLines = 1,
  bodyLines = 3,
}: SectionCardSkeletonProps): ReactElement {
  return (
    <Card aria-hidden="true">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3">
          <Skeleton className="h-5 w-44" />
          <Skeleton className="h-8 w-24 rounded-md" />
        </div>
        {headerLines > 1 && <Skeleton className="h-3 w-1/2" />}
      </CardHeader>
      <CardContent className="space-y-2.5">
        {Array.from({ length: bodyLines }, (_, idx) => (
          <Skeleton
            key={idx}
            className={
              idx % 3 === 0
                ? 'h-3.5 w-3/4'
                : idx % 3 === 1
                  ? 'h-3.5 w-2/3'
                  : 'h-3.5 w-1/2'
            }
          />
        ))}
      </CardContent>
    </Card>
  );
}
